/**
 * StepCard - 流程步驟卡片組件
 * 
 * 顯示單個審批步驟，支持：
 * 1. 顯示步驟名稱和審批人
 * 2. 展開/收合詳細信息
 * 3. 編輯審批人
 * 4. 插入步驟按鈕
 */

import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ApproverBadgeGroup } from './ApproverBadge';
import { useI18n, getText } from '../../context/I18nContext';

// 步驟類型圖標
const StepTypeIcon = ({ type, status }) => {
    const baseClass = "w-8 h-8 rounded-full flex items-center justify-center";
    
    const statusColors = {
        completed: 'bg-green-100 text-green-600',
        current: 'bg-blue-100 text-blue-600',
        pending: 'bg-gray-100 text-gray-400',
        skipped: 'bg-gray-100 text-gray-300',
    };

    const colorClass = statusColors[status] || statusColors.pending;

    const icons = {
        submit: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
        ),
        approval: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        completed: (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
        ),
    };

    return (
        <div className={`${baseClass} ${colorClass}`}>
            {icons[type] || icons.approval}
        </div>
    );
};

/**
 * StepCard - 步驟卡片組件
 * 
 * @param {Object} props
 * @param {Object} props.step - 步驟數據
 * @param {number} props.index - 步驟索引
 * @param {boolean} props.canEdit - 是否可編輯
 * @param {boolean} props.canDrag - 是否可拖拉
 * @param {boolean} props.isExpanded - 是否展開
 * @param {Function} props.onToggleExpand - 展開/收合回調
 * @param {Function} props.onEditApprovers - 編輯審批人回調
 * @param {Function} props.onAddApprover - 新增審批人回調
 * @param {Function} props.onRemoveApprover - 移除審批人回調
 * @param {Function} props.onReorderApprovers - 重新排序審批人回調
 * @param {Function} props.onInsertBefore - 在此步驟前插入回調
 * @param {Function} props.onInsertAfter - 在此步驟後插入回調
 * @param {Function} props.onRemoveStep - 刪除新增步驟回調
 * @param {Function} props.getText - 多語言文字解析函數
 * @param {Object} props.flowSettings - 流程設定
 */
export default function StepCard({
    step,
    index,
    canEdit = false,
    canDrag = false,
    isExpanded = false,
    onToggleExpand,
    onEditApprovers,
    onAddApprover,
    onRemoveApprover,
    onReorderApprovers,
    onInsertBefore,
    onInsertAfter,
    onRemoveStep,
    getText,
    flowSettings = {},
}) {
    const { t } = useI18n(['common', 'forms']);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ 
        id: step.stepId,
        disabled: !canDrag
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const isSkipped = step.status === 'skipped' || step.isSkipped;
    const stepEditing = step.stepEditing || {};
    
    // 計算實際可編輯權限
    const actualCanEdit = canEdit && !isSkipped && step.stepType === 'approval';
    const canModifyApprover = actualCanEdit && (stepEditing.canModifyApprover ?? flowSettings.allowModifyApprover ?? true);
    const canAddApproverToStep = actualCanEdit && (stepEditing.canAddApprover ?? flowSettings.allowAddApproverToStep ?? true);
    const canInsertBeforeStep = canEdit && (stepEditing.canInsertBefore ?? flowSettings.allowInsertStep ?? true);
    const canInsertAfterStep = canEdit && (stepEditing.canInsertAfter ?? flowSettings.allowInsertStep ?? true);
    const canRemoveStep = canEdit && step.isAdded && (stepEditing.canRemove ?? true);

    // 狀態樣式
    const statusStyles = {
        completed: 'border-l-green-500 bg-green-50/50',
        current: 'border-l-blue-500 bg-blue-50/50',
        pending: 'border-l-gray-300 bg-white',
        skipped: 'border-l-gray-200 bg-gray-50 opacity-60',
    };

    const stepName = getText ? getText(step.stepName) : step.stepName;
    const approvers = step.approvers || [];

    return (
        <div className="relative group">
            {/* 插入前按鈕 */}
            {canInsertBeforeStep && step.stepType !== 'submit' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={() => onInsertBefore?.(step, index)}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white text-xs rounded-full shadow-lg hover:bg-blue-700"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {t('forms:route.insertBefore') || '插入'}
                    </button>
                </div>
            )}

            {/* 主卡片 */}
            <div
                ref={setNodeRef}
                style={style}
                className={`relative border-l-4 rounded-lg shadow-sm p-4 ${statusStyles[step.status] || statusStyles.pending} ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''}`}
            >
                {/* 頂部區域 */}
                <div className="flex items-start gap-3">
                    {/* 拖拉手柄 */}
                    {canDrag && (
                        <div
                            {...attributes}
                            {...listeners}
                            className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 mt-1"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                            </svg>
                        </div>
                    )}

                    {/* 步驟圖標 */}
                    <StepTypeIcon type={step.stepType} status={step.status} />

                    {/* 步驟信息 */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                                {t('forms:route.step') || '步驟'} {index + 1}
                            </span>
                            {isSkipped && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-500 text-xs rounded">
                                    {t('forms:route.skipped') || '已跳過'}
                                </span>
                            )}
                            {step.isOverridden && !isSkipped && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded">
                                    {t('forms:route.modified') || '已修改'}
                                </span>
                            )}
                            {step.isAdded && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                    {t('forms:route.added') || '新增'}
                                </span>
                            )}
                        </div>
                        <h4 className={`font-medium text-gray-900 ${isSkipped ? 'line-through' : ''}`}>
                            {stepName}
                        </h4>
                        
                        {/* 審批人摘要 */}
                        {approvers.length > 0 && !isExpanded && (
                            <div className="mt-1 text-sm text-gray-500">
                                {approvers.slice(0, 3).map(a => a.displayName).join(', ')}
                                {approvers.length > 3 && ` +${approvers.length - 3}`}
                            </div>
                        )}
                    </div>

                    {/* 右側操作按鈕 */}
                    <div className="flex items-center gap-1">
                        {canRemoveStep && !isSkipped && (
                            <button
                                onClick={() => onRemoveStep?.(step.stepId)}
                                className="p-2 text-red-400 hover:text-red-600 rounded"
                                title={t('forms:route.removeStep') || '刪除步驟'}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                        {step.stepType === 'approval' && !isSkipped && (
                            <button
                                onClick={() => onToggleExpand?.(step.stepId)}
                                className="p-2 text-gray-400 hover:text-gray-600 rounded"
                            >
                                <svg 
                                    className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* 展開內容 */}
                {isExpanded && step.stepType === 'approval' && !isSkipped && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <ApproverBadgeGroup
                            approvers={approvers}
                            status={step.status}
                            approvalMode={step.approvalMode}
                            canEdit={canModifyApprover}
                            onEdit={(approver) => onEditApprovers?.(step, approver)}
                            onRemove={(approver) => onRemoveApprover?.(step, approver)}
                            onAdd={canAddApproverToStep ? () => onAddApprover?.(step) : undefined}
                            onReorder={(newApprovers) => onReorderApprovers?.(step, newApprovers)}
                        />

                        {/* 審批模式選擇 */}
                        {canModifyApprover && approvers.length > 1 && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                                <label className="block text-xs text-gray-500 mb-2">
                                    {t('forms:route.approvalMode') || '審批模式'}
                                </label>
                                <select
                                    value={step.approvalMode || 'sequential'}
                                    onChange={(e) => onEditApprovers?.(step, null, e.target.value)}
                                    className="w-full text-sm border rounded px-2 py-1"
                                >
                                    <option value="sequential">{t('forms:route.modeSequential') || '依序審批'}</option>
                                    <option value="parallel_any">{t('forms:route.modeParallelAny') || '任一審批'}</option>
                                    <option value="parallel_all">{t('forms:route.modeParallelAll') || '全部審批'}</option>
                                </select>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 插入後按鈕 */}
            {canInsertAfterStep && step.stepType !== 'completed' && (
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={() => onInsertAfter?.(step, index)}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white text-xs rounded-full shadow-lg hover:bg-blue-700"
                    >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {t('forms:route.insertAfter') || '插入'}
                    </button>
                </div>
            )}
        </div>
    );
}
